import React, { useState } from "react";
import { HelpCircle } from "lucide-react";
import { scoreBand } from "@/components/site/Stars";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

/** Shared content shown inside the "Arti skor" popover. Kept as a separate export so any
 * trigger element (help button, review composite pill, etc.) can reuse it. */
export function ScoreLegendContent() {
  const bands = [
    { range: "4.5 – 5.0", v: 5 },
    { range: "3.5 – 4.4", v: 4 },
    { range: "2.5 – 3.4", v: 3 },
    { range: "< 2.5",     v: 2 },
  ];
  return (
    <div data-testid="score-legend-content">
      <div className="text-[10px] uppercase tracking-widest text-[#4B5563] mb-2">Arti skor komposit</div>
      <div className="space-y-1.5">
        {bands.map(({ range, v }) => {
          const b = scoreBand(v);
          return (
            <div key={range} className="flex items-center gap-2" data-testid={`score-legend-band-${b.key}`}>
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border font-semibold"
                    style={{ backgroundColor: b.bg, borderColor: b.border, color: b.text }}>
                {b.label}
              </span>
              <span className="text-[11px] text-[#4B5563] tabular-nums ml-auto">{range}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-3 pt-3 border-t border-[#EAE7E0] text-[11px] text-[#4B5563] italic leading-snug">
        Skor komposit adalah rata-rata dari seluruh kategori rating (Pengajaran, Fasilitas, Keamanan, dst.) yang diisi oleh Orang Tua, Siswa Aktif, dan Alumni.
      </div>
    </div>
  );
}

/**
 * Tap/click-to-open explainer of the 4 score bands. By default it renders a small "Arti skor"
 * text-button trigger; callers can provide a custom `children` element to be the trigger instead
 * (e.g. wrap a review composite pill so it also opens the legend on click).
 */
export default function ScoreLegend({ children, triggerTestid = "score-legend-trigger" }) {
  const [open, setOpen] = useState(false);
  // When a custom trigger is passed as children, propagate the triggerTestid onto it so callers
  // can target specific popover triggers by testid (e.g. per-review composite pills).
  const triggerChild = children
    ? React.cloneElement(children, { ...(children.props || {}), "data-testid": triggerTestid })
    : (
      <button type="button" data-testid={triggerTestid}
              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#4B5563] hover:text-[#1E3A8A] transition-colors">
        <HelpCircle size={12} /> Arti skor
      </button>
    );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerChild}
      </PopoverTrigger>
      <PopoverContent className="w-72 rounded-2xl border-[#EAE7E0] shadow-lg" data-testid="score-legend-popover">
        <ScoreLegendContent />
      </PopoverContent>
    </Popover>
  );
}
